import React, { useState, useEffect } from "react";
import apiService from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";

const InventoryReports = () => {
  const [activeTab, setActiveTab] = useState("stock-summary");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  // Report data states
  const [stockSummary, setStockSummary] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [movement, setMovement] = useState(null);
  const [reorderLevel, setReorderLevel] = useState(null);
  const [slowMoving, setSlowMoving] = useState(null);
  const [fastMoving, setFastMoving] = useState(null);
  const [aging, setAging] = useState(null);
  const [stockCountSheet, setStockCountSheet] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    warehouse: "",
    location: "",
    category: "",
    costMethod: "FIFO",
    days: 90,
    minMovement: 10,
    period: "month",
    includeZeroStock: false,
  });

  const fetchReport = async (reportType) => {
    setLoading(true);
    setError("");

    try {
      let endpoint = "";
      let params = new URLSearchParams();

      // Add common filters
      if (filters.warehouse) params.append("warehouse", filters.warehouse);
      if (filters.location) params.append("location", filters.location);
      if (filters.category) params.append("category", filters.category);

      switch (reportType) {
        case "stock-summary":
          endpoint = `/inventory/reports/stock-summary?${params}`;
          break;
        case "valuation":
          params.append("costMethod", filters.costMethod);
          endpoint = `/inventory/reports/valuation?${params}`;
          break;
        case "movement":
          params.append("startDate", dateRange.startDate);
          params.append("endDate", dateRange.endDate);
          endpoint = `/inventory/reports/movement?${params}`;
          break;
        case "reorder-level":
          endpoint = `/inventory/reports/reorder-level?${params}`;
          break;
        case "slow-moving":
          params.append("days", filters.days);
          endpoint = `/inventory/reports/slow-moving?${params}`;
          break;
        case "fast-moving":
          params.append("days", filters.days);
          params.append("minMovement", filters.minMovement);
          endpoint = `/inventory/reports/fast-moving?${params}`;
          break;
        case "aging":
          endpoint = `/inventory/reports/aging?${params}`;
          break;
        case "stock-count-sheet":
          params.append("period", filters.period);
          params.append("includeZeroStock", filters.includeZeroStock);
          if (dateRange.startDate && dateRange.endDate) {
            params.append("startDate", dateRange.startDate);
            params.append("endDate", dateRange.endDate);
          }
          endpoint = `/inventory/reports/stock-count-sheet?${params}`;
          break;
        default:
          throw new Error("Invalid report type");
      }

      const data = await apiService.get(endpoint);

      switch (reportType) {
        case "stock-summary":
          setStockSummary(data);
          break;
        case "valuation":
          setValuation(data);
          break;
        case "movement":
          setMovement(data);
          break;
        case "reorder-level":
          setReorderLevel(data);
          break;
        case "slow-moving":
          setSlowMoving(data);
          break;
        case "fast-moving":
          setFastMoving(data);
          break;
        case "aging":
          setAging(data);
          break;
        case "stock-count-sheet":
          setStockCountSheet(data);
          break;
      }
    } catch (err) {
      setError(`Failed to load ${reportType} report: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(activeTab);
  }, [activeTab, dateRange, filters]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN").format(num);
  };

  const renderStockSummary = () => {
    if (!stockSummary) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Items</h3>
            <p className="text-2xl font-bold text-blue-600">
              {formatNumber(stockSummary.totalItems)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Value</h3>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(stockSummary.totalValue)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Low Stock Items
            </h3>
            <p className="text-2xl font-bold text-orange-600">
              {formatNumber(stockSummary.lowStockItems)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Out of Stock</h3>
            <p className="text-2xl font-bold text-red-600">
              {formatNumber(stockSummary.outOfStockItems)}
            </p>
          </div>
        </div>

        {/* Stock Summary Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Stock Summary Report
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Qty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Available
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reserved
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stockSummary.stockSummary.map((item, index) => (
                  <tr
                    key={item.productSkuId}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.productName}
                        </div>
                        <div className="text-sm text-gray-500">{item.sku}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.totalQuantity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.availableQuantity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.reservedQuantity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(item.totalValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.stockStatus === "Out of Stock"
                            ? "bg-red-100 text-red-800"
                            : item.stockStatus === "Low Stock"
                            ? "bg-orange-100 text-orange-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {item.stockStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderValuation = () => {
    if (!valuation) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Total Value ({filters.costMethod})
            </h3>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(valuation.totalValue)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Items</h3>
            <p className="text-2xl font-bold text-blue-600">
              {formatNumber(valuation.totalItems)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">FIFO Total</h3>
            <p className="text-2xl font-bold text-purple-600">
              {formatCurrency(valuation.summary.fifoTotal)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">LIFO Total</h3>
            <p className="text-2xl font-bold text-orange-600">
              {formatCurrency(valuation.summary.lifoTotal)}
            </p>
          </div>
        </div>

        {/* Valuation Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Inventory Valuation Report ({filters.costMethod})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    FIFO Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LIFO Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Selected Value
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {valuation.valuationReport.map((item, index) => (
                  <tr
                    key={item.productSkuId}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.productName}
                        </div>
                        <div className="text-sm text-gray-500">{item.sku}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.totalQuantity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(item.averageCost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(item.fifoValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(item.lifoValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(item.selectedValue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderStockCountSheet = () => {
    if (!stockCountSheet) return null;

    return (
      <div className="space-y-6">
        {/* Period Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            Stock Count Sheet Period
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-blue-700">Period Type:</span>{" "}
              {stockCountSheet.period.type}
            </div>
            <div>
              <span className="font-medium text-blue-700">Start Date:</span>{" "}
              {new Date(stockCountSheet.period.start).toLocaleDateString()}
            </div>
            <div>
              <span className="font-medium text-blue-700">End Date:</span>{" "}
              {new Date(stockCountSheet.period.end).toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Total Products
            </h3>
            <p className="text-2xl font-bold text-blue-600">
              {formatNumber(stockCountSheet.summary.totalProducts)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Opening Stock Value
            </h3>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(stockCountSheet.summary.totalOpeningStockValue)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Current Stock Value
            </h3>
            <p className="text-2xl font-bold text-purple-600">
              {formatCurrency(stockCountSheet.summary.totalCurrentStockValue)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Stock Variance
            </h3>
            <p
              className={`text-2xl font-bold ${
                stockCountSheet.summary.totalVarianceValue >= 0
                  ? "text-red-600"
                  : "text-green-600"
              }`}
            >
              {formatCurrency(
                Math.abs(stockCountSheet.summary.totalVarianceValue)
              )}
            </p>
          </div>
        </div>

        {/* Detailed Summary */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Stock Count Summary
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {formatNumber(stockCountSheet.summary.totalOpeningStock)}
                </div>
                <div className="text-sm text-gray-600">Opening Stock (Qty)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatNumber(stockCountSheet.summary.totalAdditions)}
                </div>
                <div className="text-sm text-gray-600">
                  Stock Additions (Qty)
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {formatNumber(stockCountSheet.summary.totalSales)}
                </div>
                <div className="text-sm text-gray-600">Sales (Qty)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {formatNumber(stockCountSheet.summary.totalClosingStock)}
                </div>
                <div className="text-sm text-gray-600">
                  Theoretical Closing (Qty)
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stock Count Sheet Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Stock Count Sheet
            </h2>
            <p className="text-sm text-gray-600">
              Detailed stock count analysis for the selected period
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opening
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Additions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Theoretical Closing
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actual Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stockCountSheet.stockCountSheet.map((item, index) => (
                  <tr
                    key={item.productSkuId}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.productName}
                        </div>
                        <div className="text-sm text-gray-500">{item.sku}</div>
                        <div className="text-xs text-gray-400">
                          {item.category}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatNumber(item.openingStock)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatCurrency(item.openingStockValue)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatNumber(item.stockAdditions)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatCurrency(item.stockAdditionsValue)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatNumber(item.salesQuantity)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatCurrency(item.salesValue)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatNumber(item.closingStock)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatCurrency(item.closingStockValue)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatNumber(item.currentStock)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatCurrency(item.currentStockValue)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`text-sm font-medium ${
                          item.stockVariance >= 0
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {item.stockVariance >= 0 ? "+" : ""}
                        {formatNumber(item.stockVariance)}
                      </div>
                      <div
                        className={`text-xs ${
                          item.stockVarianceValue >= 0
                            ? "text-red-500"
                            : "text-green-500"
                        }`}
                      >
                        {item.stockVarianceValue >= 0 ? "+" : ""}
                        {formatCurrency(Math.abs(item.stockVarianceValue))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.stockVariance === 0
                            ? "bg-green-100 text-green-800"
                            : item.stockVariance > 0
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {item.stockVariance === 0
                          ? "Balanced"
                          : item.stockVariance > 0
                          ? "Overstock"
                          : "Understock"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Category Analysis */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Category Analysis
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Products
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opening Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Additions Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stockCountSheet.categoryAnalysis.map((category, index) => (
                  <tr
                    key={category.category}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {category.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(category.items.length)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(category.totalOpeningStockValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(category.totalAdditionsValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(category.totalSalesValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(category.totalCurrentStockValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`text-sm font-medium ${
                          category.totalVarianceValue >= 0
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {category.totalVarianceValue >= 0 ? "+" : ""}
                        {formatCurrency(Math.abs(category.totalVarianceValue))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderMovement = () => {
    if (!movement || !movement.movements) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Total Movements
            </h3>
            <p className="text-2xl font-bold text-blue-600">
              {formatNumber(movement.summary?.totalMovements || 0)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total In</h3>
            <p className="text-2xl font-bold text-green-600">
              {formatNumber(movement.summary?.totalIn || 0)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Out</h3>
            <p className="text-2xl font-bold text-red-600">
              {formatNumber(movement.summary?.totalOut || 0)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Net Movement</h3>
            <p
              className={`text-2xl font-bold ${
                movement.summary?.totalIn - movement.summary?.totalOut >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {formatNumber(
                (movement.summary?.totalIn || 0) -
                  (movement.summary?.totalOut || 0)
              )}
            </p>
          </div>
        </div>

        {/* Movement Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Stock Movement History
            </h2>
            <p className="text-sm text-gray-600">
              Showing movements from{" "}
              {new Date(dateRange.startDate).toLocaleDateString()} to{" "}
              {new Date(dateRange.endDate).toLocaleDateString()}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {movement.movements.map((item, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(
                        item.date || item.createdAt
                      ).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.productName || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.sku || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.type === "IN"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {item.type || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium ${
                          item.type === "IN" ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {item.type === "IN" ? "+" : "-"}
                        {formatNumber(item.quantity || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.location || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.reference || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderReorderLevel = () => {
    if (!reorderLevel || !reorderLevel.reorderReport) return null;

    const items = reorderLevel.reorderReport;
    const criticalItems = items.filter((item) => item.urgency === "Critical");
    const highItems = items.filter((item) => item.urgency === "High");

    return (
      <div className="space-y-6">
        {/* Alert Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-red-700">Critical Stock</h3>
            <p className="text-2xl font-bold text-red-600">
              {criticalItems.length}
            </p>
            <p className="text-xs text-red-600">Below reorder level</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-700">
              High Priority
            </h3>
            <p className="text-2xl font-bold text-yellow-600">
              {highItems.length}
            </p>
            <p className="text-xs text-yellow-600">Below minimum stock</p>
          </div>
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-700">
              Total Items Monitored
            </h3>
            <p className="text-2xl font-bold text-green-600">{items.length}</p>
            <p className="text-xs text-green-600">Across all locations</p>
          </div>
        </div>

        {/* Reorder Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Reorder Level Report
            </h2>
            <p className="text-sm text-gray-600">
              Items requiring attention for restocking
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Available Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Min Stock Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reorder Qty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Urgency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Locations
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.productName || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.sku || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.availableQuantity || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.minStock || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium ${
                          item.reorderQuantity > 0
                            ? "text-orange-600"
                            : "text-green-600"
                        }`}
                      >
                        {formatNumber(item.reorderQuantity || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.reorderStatus === "Out of Stock"
                            ? "bg-red-100 text-red-800"
                            : item.reorderStatus === "Reorder Required"
                            ? "bg-orange-100 text-orange-800"
                            : item.reorderStatus === "Monitor"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {item.reorderStatus || "Normal"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-bold rounded ${
                          item.urgency === "Critical"
                            ? "bg-red-600 text-white"
                            : item.urgency === "High"
                            ? "bg-orange-500 text-white"
                            : item.urgency === "Medium"
                            ? "bg-yellow-500 text-white"
                            : "bg-green-500 text-white"
                        }`}
                      >
                        {item.urgency || "Low"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.locations && item.locations.length > 0
                        ? item.locations.join(", ")
                        : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderSlowMoving = () => {
    if (!slowMoving || !slowMoving.slowMovingReport) return null;

    const items = slowMoving.slowMovingReport;
    const totalValue = items.reduce(
      (sum, item) => sum + (item.totalValue || 0),
      0
    );
    const totalQuantity = items.reduce(
      (sum, item) => sum + (item.currentStock || 0),
      0
    );
    const avgDays =
      items.length > 0
        ? Math.round(
            items.reduce(
              (sum, item) => sum + (item.daysSinceLastMovement || 0),
              0
            ) / items.length
          )
        : 0;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Slow Moving Items
            </h3>
            <p className="text-2xl font-bold text-orange-600">
              {formatNumber(items.length)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Total Stock Value
            </h3>
            <p className="text-2xl font-bold text-red-600">
              {formatCurrency(totalValue)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Total Quantity
            </h3>
            <p className="text-2xl font-bold text-purple-600">
              {formatNumber(totalQuantity)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Avg Days in Stock
            </h3>
            <p className="text-2xl font-bold text-blue-600">
              {formatNumber(avgDays)}
            </p>
          </div>
        </div>

        {/* Slow Moving Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Slow-Moving Items Report
            </h2>
            <p className="text-sm text-gray-600">
              Items with low turnover (more than {filters.days} days without
              movement)
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock Qty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days in Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Movement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.productName || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.sku || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.category || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.currentStock || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                      {formatCurrency(item.totalValue || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium ${
                          item.daysSinceLastMovement > 180
                            ? "text-red-600"
                            : item.daysSinceLastMovement > 90
                            ? "text-orange-600"
                            : "text-yellow-600"
                        }`}
                      >
                        {formatNumber(item.daysSinceLastMovement || 0)} days
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.lastMovement
                        ? new Date(item.lastMovement).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.location || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderFastMoving = () => {
    if (!fastMoving || !fastMoving.fastMovingReport) return null;

    const items = fastMoving.fastMovingReport || [];

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Fast Moving Items
            </h3>
            <p className="text-2xl font-bold text-green-600">
              {formatNumber(items.length)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Total Movement
            </h3>
            <p className="text-2xl font-bold text-blue-600">
              {formatNumber(fastMoving.totalMovement || 0)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Critical Stock
            </h3>
            <p className="text-2xl font-bold text-red-600">
              {formatNumber(fastMoving.criticalStockItems || 0)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Low Stock Items
            </h3>
            <p className="text-2xl font-bold text-orange-600">
              {formatNumber(fastMoving.lowStockItems || 0)}
            </p>
          </div>
        </div>

        {/* Fast Moving Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Fast-Moving Items Report
            </h2>
            <p className="text-sm text-gray-600">
              High turnover items (minimum {filters.minMovement} movements in
              selected period)
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Sold
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Daily Avg
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          index === 0
                            ? "bg-yellow-100 text-yellow-800"
                            : index === 1
                            ? "bg-gray-100 text-gray-800"
                            : index === 2
                            ? "bg-orange-100 text-orange-800"
                            : "bg-blue-50 text-blue-800"
                        }`}
                      >
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.productName || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.sku || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.category || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatNumber(item.totalMovement || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.currentStock || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.dailyAverage || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                      {formatCurrency(item.totalValue || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.stockStatus || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderAging = () => {
    if (!aging || !aging.agingReport) return null;

    const items = aging.agingReport || [];

    return (
      <div className="space-y-6">
        {/* Summary Cards with Age Buckets */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Items</h3>
            <p className="text-2xl font-bold text-blue-600">
              {formatNumber(aging.totalItems || 0)}
            </p>
            <p className="text-xs text-gray-600">
              {formatCurrency(aging.totalValue || 0)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Expired Items</h3>
            <p className="text-2xl font-bold text-red-600">
              {formatNumber(aging.expiredItems || 0)}
            </p>
            <p className="text-xs text-red-600">Immediate action</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Expiring Soon</h3>
            <p className="text-2xl font-bold text-orange-600">
              {formatNumber(aging.expiringSoonItems || 0)}
            </p>
            <p className="text-xs text-orange-600">Within 30 days</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Old Stock</h3>
            <p className="text-2xl font-bold text-yellow-600">
              {formatNumber(aging.oldStockItems || 0)}
            </p>
            <p className="text-xs text-yellow-600">Over 1 year</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">
              Age Categories
            </h3>
            <p className="text-2xl font-bold text-purple-600">
              {formatNumber(aging.ageCategories?.length || 0)}
            </p>
            <p className="text-xs text-gray-600">Different buckets</p>
          </div>
        </div>

        {/* Aging Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Stock Aging Report
            </h2>
            <p className="text-sm text-gray-600">
              Analysis of stock age across all inventory items
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Age (Days)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Age Bucket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Batch
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item, index) => {
                  const age = item.ageInDays || 0;
                  const ageBucket = item.ageCategory || "N/A";
                  let bucketColor = "bg-green-100 text-green-800";

                  if (age > 365) {
                    bucketColor = "bg-red-100 text-red-800";
                  } else if (age > 180) {
                    bucketColor = "bg-orange-100 text-orange-800";
                  } else if (age > 90) {
                    bucketColor = "bg-yellow-100 text-yellow-800";
                  } else if (age > 30) {
                    bucketColor = "bg-blue-100 text-blue-800";
                  }

                  return (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {item.productName || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {item.sku || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.category || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(item.currentStock || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(item.totalValue || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`text-sm font-medium ${
                            age > 365
                              ? "text-red-600"
                              : age > 180
                              ? "text-orange-600"
                              : age > 90
                              ? "text-yellow-600"
                              : age > 30
                              ? "text-blue-600"
                              : "text-green-600"
                          }`}
                        >
                          {formatNumber(age)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${bucketColor}`}
                        >
                          {ageBucket}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.location || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.batch || "N/A"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Inventory Reports</h1>
        <p className="mt-2 text-gray-600">
          Comprehensive inventory analytics and management insights
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Warehouse
            </label>
            <input
              type="text"
              value={filters.warehouse}
              onChange={(e) =>
                setFilters({ ...filters, warehouse: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Filter by warehouse"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              type="text"
              value={filters.location}
              onChange={(e) =>
                setFilters({ ...filters, location: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Filter by location"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <input
              type="text"
              value={filters.category}
              onChange={(e) =>
                setFilters({ ...filters, category: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Filter by category"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cost Method
            </label>
            <select
              value={filters.costMethod}
              onChange={(e) =>
                setFilters({ ...filters, costMethod: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="FIFO">FIFO</option>
              <option value="LIFO">LIFO</option>
              <option value="Weighted Average">Weighted Average</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Analysis Days
            </label>
            <input
              type="number"
              value={filters.days}
              onChange={(e) => setFilters({ ...filters, days: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              min="1"
              max="365"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Movement
            </label>
            <input
              type="number"
              value={filters.minMovement}
              onChange={(e) =>
                setFilters({ ...filters, minMovement: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              min="1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Period
            </label>
            <select
              value={filters.period}
              onChange={(e) =>
                setFilters({ ...filters, period: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Year</option>
            </select>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="includeZeroStock"
              checked={filters.includeZeroStock}
              onChange={(e) =>
                setFilters({ ...filters, includeZeroStock: e.target.checked })
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="includeZeroStock"
              className="ml-2 block text-sm text-gray-700"
            >
              Include Zero Stock
            </label>
          </div>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">
            Date Range:
          </label>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) =>
              setDateRange({ ...dateRange, startDate: e.target.value })
            }
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) =>
              setDateRange({ ...dateRange, endDate: e.target.value })
            }
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {[
            { id: "stock-summary", name: "📊 Stock Summary", icon: "📊" },
            { id: "valuation", name: "💰 Inventory Valuation", icon: "💰" },
            { id: "movement", name: "🔄 Stock Movement", icon: "🔄" },
            { id: "reorder-level", name: "⚠️ Reorder Level", icon: "⚠️" },
            { id: "slow-moving", name: "🐢 Slow-Moving Items", icon: "🐢" },
            { id: "fast-moving", name: "🚀 Fast-Moving Items", icon: "🚀" },
            { id: "aging", name: "🧓 Stock Aging", icon: "🧓" },
            {
              id: "stock-count-sheet",
              name: "📋 Stock Count Sheet",
              icon: "📋",
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Report Content */}
      {!loading && !error && (
        <div>
          {activeTab === "stock-summary" && renderStockSummary()}
          {activeTab === "valuation" && renderValuation()}
          {activeTab === "movement" && renderMovement()}
          {activeTab === "reorder-level" && renderReorderLevel()}
          {activeTab === "slow-moving" && renderSlowMoving()}
          {activeTab === "fast-moving" && renderFastMoving()}
          {activeTab === "aging" && renderAging()}
          {activeTab === "stock-count-sheet" && renderStockCountSheet()}
        </div>
      )}
    </div>
  );
};

export default InventoryReports;
